<template>
  <v-navigation-drawer
    v-model="$store.state.showShortcuts"
    :overlay-opacity="0"
    :temporary="true"
    :touchless="true"
    style="z-index: 200; padding-top: 40px; border: 0;"
    width="350"
    class="elevation-0"
    color="grey lighten-2"
    app
    absolute
  >
    <v-container
      v-if="loadingShortcuts"
      class="py-5 my-5 text-center"
    >
      <v-row>
        <v-col class="text-center">
          <v-icon class="fa-spin">
            fa-spinner
          </v-icon>
        </v-col>
      </v-row>
    </v-container>

    <!-- FORM -->

    <v-container
      v-if="show_form && !loadingShortcuts"
    >
      <ShortcutAddExternalLink
        :close-form="closeForm"
      />
    </v-container>

    <!-- ACTION BUTTONS -->

    <v-container
      v-if="!show_form && !loadingShortcuts"
      class="config_buttons_container"
    >
      <v-row class="pt-1 pb-0">
        <v-col cols="3" />
        <v-col
          cols="3"
          class="text-center"
        >
          <v-btn
            fab
            x-small
            class="elevation-0"
            @click="openForm"
          >
            <v-icon
              small
              color="green"
            >
              fa-plus
            </v-icon>
          </v-btn>
        </v-col>
        <v-col
          v-if="shortcuts.length > 0"
          cols="3"
          class="text-center"
        >
          <v-btn
            fab
            x-small
            class="elevation-0"
            @click="toggleEditMode"
          >
            <v-icon
              small
              color="orange"
            >
              fa-edit
            </v-icon>
          </v-btn>
        </v-col>
        <v-col cols="3" />
      </v-row>
    </v-container>

    <!-- LIST -->

    <v-sheet
      v-if="render && !show_form && !loadingShortcuts"
      id="side-shortcuts"
      class="menu-drawer-list pb-3 elevation-0"
      color="transparent"
      nav
      block
      dense
    >
      <v-btn
        v-for="( shortcut, index ) in shortcuts"
        :key="index"
        :to="shortcut.details.link_type !== 'external' ? shortcut.link_url.replace('#', '/') : ''"
        :href="shortcut.details.link_type === 'external' ? shortcut.details.external_link : ''"
        :target="shortcut.details.link_type === 'external' ? '_blank' : ''"
        class="text-left text-left -align-left"
        max-width="350"
        dense
        tile
        text
        block
        exact
      >
        <v-icon
          v-if="edit_mode"
          class="pr-5"
          color="red lighten-1"
          small
          @click.stop.prevent="deleteShortcut(shortcut.id)"
        >
          fa-times
        </v-icon>
        <v-icon
          v-else
          color="grey darken-2"
          small
          class="pr-5"
        >
          fa-{{ shortcutIcon(shortcut) }}
        </v-icon>
        {{ shortcut.link_title }}
      </v-btn>
    </v-sheet>
  </v-navigation-drawer>
</template>

<script lang="ts">
import ShortcutAddExternalLink from '@/components/Shortcuts/ShortcutAddExternalLink.vue'
import shortcutData from '@/store/dashboardItems/helpers/shortcutData'
import Sortable from 'sortablejs'
import { DashboardTypes } from '@/types/DashboardTypes'
import dashboardMethods from '@/components/Dashboard/dashboardMethods'
import util from '../../utilities/sharedUtilities'

export default {
  name: 'ShortcutsList',

  components: {
    ShortcutAddExternalLink,
  },

  data () {
    return {
      render: true,
      showShortcuts: false,
      show_form: false,
      edit_mode: false,
      opened_new_tab_ts: 0,
    }
  },

  computed: {
    shortcuts (): DashboardTypes.Shortcut[] {
      return this.$store.state.shortcuts.filter(shortcut => {
        return shortcut.details?.link_type === 'internal' ||
          util.isValidHttpsURL(shortcut.details?.external_link) // Prevent executing JS which this same user added ;)
      })
    },
    loadingShortcuts () {
      return this.$store.state.loadingShortcuts
    },
  },

  created () {
    this.createShortcutsSortables()
  },

  methods: {
    ...dashboardMethods,

    createShortcutsSortables () {
      if (this.$store.state.isMobileOrTablet) { return }
      this.$nextTick(() => {
        this.sortable = null
        const el = document.getElementById('side-shortcuts')
        if (!el) { return }
        this.sortable = Sortable.create(el, {
          onEnd: this.shortcutDragEnd,
          // delay: 300, No need for delay as not supported on mobile
          // because holding the finger for certain time opens mobile browser menu
          // and sorting is not possible anyway
        })
      })
    },

    shortcutDragEnd (move) {
      const temp = this.shortcuts.splice(move.oldIndex, 1)[0]
      this.shortcuts.splice(move.newIndex, 0, temp)
      // Re-render for new order to be shown
      // Without it sortable new order and vuex new order are conflicted
      this.reRender()
      this.$store.commit('updateShortcutsOrder', this.shortcuts)
    },

    reRender () {
      this.render = false
      this.$nextTick(() => {
        this.render = true
        this.$nextTick(() => {
          this.createShortcutsSortables()
        })
      })
    },

    show () {
      this.showShortcuts = true
    },

    shortcutIcon (shortcut: DashboardTypes.Shortcut) {
      return shortcutData.shortcutIcon(shortcut)
    },

    hide () {
      if (!this.show_form && !this.loadingShortcuts) {
        this.showShortcuts = false
        this.show_form = false
        this.edit_mode = false
      }
    },

    closeForm () {
      this.show_form = false
      this.edit_mode = false
      this.createShortcutsSortables()
    },

    openForm () {
      this.show_form = true
    },

    toggleEditMode () {
      this.edit_mode = !this.edit_mode
    },
  }
}
</script>
